import axios from 'axios';
import * as R from 'ramda';
import message from 'antd/lib/message';
import { normalize } from 'normalizr';
import { batchActions } from 'redux-batched-actions';

import * as schemes from '../../api/schemes';
import * as actions from '../actions/requests';
import * as ApikoApi from '../../api/apiko-api';
import { closeCurrentModal } from '../../modals';
import { updateCompanyRequests } from '../actions/companies';
import { fetchAttachmentsSuccess } from '../actions/attachments';
import { fetchRequestLogsSuccess } from '../actions/request-logs';
import { fetchTasksSuccess, deleteTaskSuccess } from '../actions/tasks';
import { fetchCommentsSuccess, deleteCommentSuccess } from '../actions/comments';
import { fetchContactsSuccess, updateContactRequests } from '../actions/contacts';
import { fetchMeetingNotesSuccess, deleteMeetingNoteSuccess } from '../actions/meeting-notes';


import { getResErrorMsg } from '../../utils';

const getNextPage = ({ requests: { page } }) => page + 1;

const getRequestsFilters = ({
  requests: {
    filters: {
      statuses, sources, labels, assignees, dateRange, byLogHistory, onlyFromCompanies,
    },
  },
}) => ({
  statuses: statuses[0] && statuses.join(),
  sources: sources[0] && sources.join(),
  labels: labels[0] && labels.join(),
  assignees: assignees[0] && assignees.join(),
  start: dateRange[0] && dateRange[0].toISOString(),
  end: dateRange[1] && dateRange[1].toISOString(),
  ...(byLogHistory && { byLogHistory }),
  ...(onlyFromCompanies && { onlyFromCompanies }),
});

const commonActions = (entities, overview) => [
  fetchContactsSuccess({
    contacts: entities.contacts || {},
    notNewPage: true,
  }),
  fetchMeetingNotesSuccess({
    meetingNotes: entities.meetingNotes || {},
    notNewPage: true,
    overview,
  }),
  fetchCommentsSuccess(entities.comments),
  fetchRequestLogsSuccess({
    logs: entities.logs || {},
  }),
  fetchTasksSuccess({
    tasks: entities.tasks || {},
    notNewPage: true,
    overview,
  }),
];

/**
 * fetchRequests :: (Object, Boolean, Boolean, Number) -> (Func, Func) -> Promise Error _
 */
export const fetchRequests = (
  cancelToken,
  overview,
  polling = false,
  monthLimit,
) => (dispatch, getState) =>
  Promise.resolve(polling ? true : dispatch(actions.fetchRequestsRequest()))
    .then(() => ApikoApi.fetchRequests({
      page: polling ? 1 : getNextPage(getState()),
      ...getRequestsFilters(getState()),
      cancelToken,
      overview,
      monthLimit,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(data, schemes.requests);

      dispatch(batchActions([
        actions.fetchRequestsSuccess({
          polling,
          count: result.count,
          ids: result.requests,
          requests: entities.requests,
          existsMore: result.existsMore,
          notNewPage: polling,
        }),
        ...commonActions(entities, overview),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchRequestsFailure());
    });

/**
 * fetchIncompleteRequests :: (Object, Boolean) -> Func -> Promise Error _
 */
export const fetchIncompleteRequests = (cancelToken, polling = false) => dispatch =>
  Promise.resolve(polling ? true : dispatch(actions.fetchRequestsRequest()))
    .then(() => ApikoApi.fetchIncompleteRequests(cancelToken))
    .then((res) => {
      const { result, entities } = normalize(res, schemes.requests);
      return dispatch(batchActions([
        actions.fetchRequestsSuccess({
          polling,
          count: result.count,
          ids: result.requests,
          requests: entities.requests,
          notNewPage: polling,
        }),
        ...commonActions(entities),
      ]));
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchRequestsFailure());
    });

/**
 * searchRequests  :: (String, Object, Boolean) -> (Func, Func) -> Promise Error
 */
export const searchRequests = (searchTerm, cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(polling ? true : dispatch(actions.searchRequestsRequest()))
    .then(() => ApikoApi.fetchRequests({
      page: polling ? getState().requests.page : getNextPage(getState()),
      ...(searchTerm.length > 1 && { searchTerm }),
      ...getRequestsFilters(getState()),
      cancelToken,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(data, schemes.requests);

      dispatch(batchActions([
        actions.searchRequestsSuccess({
          polling,
          count: result.count,
          ids: result.requests,
          requests: entities.requests,
          existsMore: result.existsMore,
          totalBudget: result.totalBudget,
          notNewPage: polling,
        }),
        ...commonActions(entities),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.searchRequestsFailure());
    });

/**
 * handleRequestCreate :: Object -> Func -> Promise Error _
 */
export const handleRequestCreate = data => dispatch =>
  Promise.resolve(dispatch(actions.createRequestRequest()))
    .then(() => ApikoApi.createRequest(data))
    .then(({ files = [], ...result }) => {
      const { entities } = normalize(result, schemes.request);

      dispatch(batchActions([
        actions.createRequestSuccess(entities.requests[result._id]),
        closeCurrentModal(),
        ...commonActions(entities),
        ...(!R.isEmpty(files) ? [fetchAttachmentsSuccess(files)] : []),
      ]));

      message.success('Request successfully added');
    })
    .catch((e) => {
      dispatch(actions.createRequestFailure());

      console.log(e);
      message.error(`Fail to create request: ${getResErrorMsg(e)}`);
    });

/**
 * handleRequestUpdate :: (ID, Object) -> (Func, Func) -> Promise Error _
 */
export const handleRequestUpdate = (requestId, modifier) => (dispatch, getState) => {
  const currentRequest = R.path(['requests', 'items', requestId], getState());
  // Optimistic update
  dispatch(actions.updateRequestSuccess({ requestId, modifier }));
  dispatch(actions.updateRequestRequest());

  return ApikoApi.updateRequestById(requestId, modifier)
    .then(({ files = [], ...data }) => {
      const { entities } = normalize(
        data,
        schemes.request,
      );
      const dataToUpdate = {
        requestId,
        modifier: entities.requests[requestId],
      };

      return dispatch(batchActions([
        actions.updateRequestSuccess(dataToUpdate),
        ...commonActions(entities),
        ...(!R.isEmpty(files) ? [fetchAttachmentsSuccess(files)] : []),
      ]));
    })
    .then(() => message.success('Request successfully updated'))
    .catch((e) => {
      // Set prev version of request on error
      dispatch(actions.updateRequestSuccess({ requestId, modifier: currentRequest }));

      console.log(e);
      message.error(`Fail to update request: ${getResErrorMsg(e)}`);
    });
};

/**
 * handleRequestUpdateModal :: (ID, Object) -> (Func, Func) -> Promise Error _
 */
export const handleRequestUpdateModal = (requestId, data) => (dispatch, getState) => {
  // get Request from state
  const prevRequest = R.path(['requests', 'items', requestId], getState());

  dispatch(actions.updateRequestModalRequest());
  return ApikoApi.updateRequestById(requestId, data)
    .then(({ files = [], ...result }) => {
      const { entities } = normalize(
        result,
        schemes.request,
      );
      const dataToUpdate = {
        requestId,
        modifier: entities.requests[requestId],
      };

      const oldRequestContactsIds = [...prevRequest.contacts];
      const newRequestContactsIds = entities.requests[requestId].contacts;

      const allContactsFromState = R.path(['contacts', 'items'], getState());

      const oldRequestContacts = oldRequestContactsIds.map(id =>
        allContactsFromState[id]).filter(item => item);
      const newRequestContacts = newRequestContactsIds.map(id =>
        allContactsFromState[id]).filter(item => item);

      return dispatch(batchActions([
        closeCurrentModal(),
        actions.updateRequestModalSuccess(dataToUpdate),
        updateContactRequests({
          requestId,
          oldRequestContactsIds,
          newRequestContactsIds,
        }),
        updateCompanyRequests({
          requestId,
          oldRequestContacts,
          newRequestContacts,
          oldRequestContactsIds,
          newRequestContactsIds,
          type: 'request-update',
        }),
        ...commonActions(entities),
        ...(!R.isEmpty(files) ? [fetchAttachmentsSuccess(files)] : []),
      ]));
    })
    .then(() => message.success('Request successfully updated'))
    .catch((e) => {
      dispatch(actions.updateRequestModalFailure());

      console.log(e);
      message.error(`Fail to update request: ${getResErrorMsg(e)}`);
    });
};

/**
 * handleRequestDelete :: ID -> (Func, Func) -> Promise Error _
 */
export const handleRequestDelete = requestId => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.deleteRequestRequest()))
    .then(() => ApikoApi.deleteRequestById(requestId))
    .then(() => {
      // get Request from state
      const request = R.pathOr([], ['requests', 'items', requestId], getState());

      const requestContactsIds = R.pathOr([], ['contacts'], request);

      const contactsFromState = R.path(['contacts', 'items'], getState());

      const requestContacts = requestContactsIds.map(id =>
        contactsFromState[id]).filter(item => item);

      // remove Request and all related data
      dispatch(batchActions([
        actions.deleteRequestSuccess(requestId),
        ...R.propOr([], 'tasks', request).map(_id => deleteTaskSuccess({ _id })),
        ...R.propOr([], 'comments', request).map(_id => deleteCommentSuccess({ _id })),
        ...R.propOr([], 'meetingNotes', request).map(_id => deleteMeetingNoteSuccess({ _id })),
        updateCompanyRequests({
          requestId,
          type: 'request-delete',
          oldRequestContacts: requestContacts,
        }),
      ]));
    })
    .then(() => message.success('Request successfully removed'))
    .catch((e) => {
      console.log(e);
      message.error(`Fail to remove request: ${getResErrorMsg(e)}`);

      dispatch(actions.deleteRequestFailure());
    });

/**
 * fetchRequestById :: (Object, String, Boolean) -> Func -> Promise Error _
 */
export const fetchRequestById = (cancelToken, requestId, polling = false) => dispatch =>
  Promise.resolve(!polling && dispatch(actions.fetchRequestsRequest()))
    .then(() => ApikoApi.fetchRequestById(requestId, cancelToken))
    .then(({ data, responseTime }) => {
      const { entities } = normalize(data, schemes.request);

      dispatch(batchActions([
        actions.fetchRequestsSuccess({
          ids: [],
          notNewPage: true,
          existsMore: false,
          requests: entities.requests || {},
        }),
        ...commonActions(entities),
        ...(!R.isEmpty(entities.attachments)
          ? [fetchAttachmentsSuccess(entities.attachments)]
          : []),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) {
        dispatch(actions.fetchRequestsFailure());

        throw new Error();
      }
    });

/**
 * handleAddRequestAssignee :: (String, String) -> (Func, Func) -> Promise Error _
 */
export const handleAddRequestAssignee = (requestId, assigneeId) => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.updateRequestRequest()))
    .then(() => ApikoApi.addRequestAssignee(requestId, assigneeId))
    .then(() => {
      const request = R.path(['requests', 'items', requestId], getState());
      const updatedRequest = {
        ...request,
        assignees: R.append(assigneeId, request.assignees),
      };

      dispatch(actions.updateRequestSuccess({
        requestId,
        modifier: updatedRequest,
      }));
    })
    .catch((e) => {
      dispatch(actions.updateRequestFailure());
      message.error(`Fail to update request: ${getResErrorMsg(e)}`);
    });

/**
 * handleRemoveRequestAssignee :: (ID, ID) -> (Func, Func) -> Promise Error _
 */
export const handleRemoveRequestAssignee = (requestId, assigneeId) => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.updateRequestRequest()))
    .then(() => ApikoApi.removeRequestAssignee(requestId, assigneeId))
    .then(() => {
      const request = R.path(['requests', 'items', requestId], getState());
      const updatedRequest = {
        ...request,
        assignees: R.reject(R.equals(assigneeId), request.assignees),
      };

      dispatch(actions.updateRequestSuccess({
        requestId,
        modifier: updatedRequest,
      }));
    })
    .catch((e) => {
      dispatch(actions.updateRequestFailure());
      message.error(`Fail to update request: ${getResErrorMsg(e)}`);
    });


/**
 * handleRemoveRequestTalent :: (ID, ID) -> (Func, Func) -> Promise Error _
 */
export const handleRemoveRequestTalent = (requestId, talentId) => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.updateRequestRequest()))
    .then(() => ApikoApi.removeRequestTalent(requestId, talentId))
    .then(() => {
      const request = R.path(['requests', 'items', requestId], getState());
      const updatedRequest = {
        ...request,
        talents: R.filter(({ _id }) => !R.equals(talentId, _id), request.talents),
      };

      dispatch(actions.updateRequestSuccess({
        requestId,
        modifier: updatedRequest,
      }));
    })
    .catch((e) => {
      dispatch(actions.updateRequestFailure());
      message.error(`Fail to update request: ${getResErrorMsg(e)}`);
    });



// WEBPACK FOOTER //
// ./src/redux/thunk/requests.js